import React from 'react';
import { Activity, Droplet, Dumbbell, Heart, Brain, Shield, BarChart3, Users, Gift, Plug, Lock } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  image?: string;
}

interface FeatureCardWithIdProps extends FeatureCardProps {
  id?: string;
}

const FeatureCard: React.FC<FeatureCardWithIdProps> = ({ icon, title, description, image, id }) => {
  return (
    <div id={id} className="group bg-white dark:bg-slate-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-slate-700 overflow-hidden scroll-mt-20">
      {image && (
        <div className="relative h-40 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-600/20 to-transparent"></div>
        </div>
      )}
      <div className="p-6">
        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4 group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 transition-colors">
          <div className="text-purple-600 dark:text-purple-400 group-hover:text-white transition-colors">
            {icon}
          </div>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          {description}
        </p>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  const holisticFeatures = [
    {
      id: 'activity-tracking',
      icon: <Activity className="w-6 h-6" />,
      title: 'Activity Tracking',
      description: 'Record each activity, document advancement. Monitor steps, mileage, energy expenditure, and movement duration accurately. Seamless integration with activity trackers and health applications. Live synchronization, past performance patterns, and customized recommendations. Establish day-to-day and week-by-week objectives, get advancement alerts, and honor achievements. Detailed movement information assists staff in comprehending their activity behaviors and making educated choices about their wellness path.',
      image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&h=400&fit=crop',
    },
    {
      id: 'nutrition',
      icon: <Droplet className="w-6 h-6" />,
      title: 'Nutrition & Hydration',
      description: 'Nourish, refresh, continue. Oversee fluid consumption and dietary objectives to sustain peak wellness. Document food intake, record energy values, and track nutrient composition. Configure water intake alerts, explore nutritious meal ideas, and obtain tailored dietary guidance. Connection with leading food tracking applications for effortless monitoring. Informative materials covering balanced nutrition, serving sizes, and selecting healthier options during work hours.',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&h=400&fit=crop',
    },
    {
      id: 'fitness',
      icon: <Dumbbell className="w-6 h-6" />,
      title: 'Fitness & Exercise',
      description: 'Your exercise path, accessible instantly. Document training sessions, establish targets, and monitor your development. Accommodation for multiple activity categories such as aerobic exercise, resistance work, flexibility practices, and additional forms. Develop personalized training schedules, pursue structured programs, and measure performance indicators. Link with exercise networks, distribute accomplishments, and gain encouragement from colleagues. Reach training video collections, movement databases, and professional advice to assist staff in remaining active and healthy.',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop',
    },
    {
      id: 'health-metrics',
      icon: <Heart className="w-6 h-6" />,
      title: 'Health Metrics',
      description: 'Mold wellness through meaningful measurements. Observe pulse rate, rest quality, and general health patterns. Document cardiovascular readings, body mass, body composition index, and additional physiological indicators. Display health information via visualizations and diagrams, detect trends, and obtain warnings for problematic patterns. Linkage with health gadgets and medical software. Thorough health summaries aid both staff and management in grasping overall wellness condition and making evidence-based health choices.',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop',
    },
    {
      id: 'mental-wellbeing',
      icon: <Brain className="w-6 h-6" />,
      title: 'Mental Well-being',
      description: 'Attain equilibrium, find your calm. Contemplation practices, tension monitoring, and awareness activities. Reach structured meditation guides, respiration methods, and calming approaches. Monitor emotional states and pressure indicators, recognize catalysts, and formulate management tactics. Psychological wellness materials, articles, and specialist recommendations. Confidential assistance pathways and emergency support. Develop a work environment that emphasizes mental health and equips staff with resources to handle stress, worry, and preserve emotional stability.',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&h=400&fit=crop',
    },
  ];

  const otherFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Accessibility',
      description: 'Facilitate smooth wellness experiences across different gadgets, languages, and regions. Cross-platform compatibility for iOS, Android, and Web. Adaptive layouts guarantee ideal interaction on mobile phones, tablets, and computers. Multilingual interfaces for international teams. Offline functionality for zones with restricted internet access. Inclusive options including audio narration support, enhanced visibility settings, and modifiable text dimensions. Guarantee all staff can engage irrespective of device, geography, or capability.',
      image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Admin Dashboard',
      description: 'Provide wellness managers with comprehensive utilities for streamlined initiative oversight. Live statistics and thorough documentation. Observe enrollment percentages, interaction measures, and initiative performance. Design and oversee competitions, configure incentives, and interact with staff. Participant administration, group structuring, and material personalization. Transfer information for additional examination, arrange automatic documentation, and measure return on investment. Advanced administrative features that simplify wellness program management and enhance efficiency.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Engagement Tools',
      description: 'Nurture a dynamic network, coordinate competitions, and honor achievements. Interactive elements that boost involvement. Distribute successes, images, and narratives. Respond and interact with updates, connect with coworkers, and establish wellness connections. Notifications, activity streams, and occasion schedules. Group messaging and conversation boards. Recognition capabilities for landmarks and accomplishments. Build an energetic wellness network that maintains staff participation, inspiration, and interconnection.',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop',
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: 'Incentivization & Rewards',
      description: 'Motivating beneficial habits through our creative framework of motivators and acknowledgments. Credits, emblems, and appreciation initiatives. Accumulate credits for actions, competitions, and regular involvement. Exchange credits for retail vouchers, organizational merchandise, additional leave, or philanthropic gifts. Progressive accomplishment tiers reveal special incentives. Emblem gathering mechanism honors diverse achievements. Ranking displays generate amicable rivalry. Appreciation functions spotlight leading contributors. Transform wellness into a gratifying and enjoyable pursuit, promoting sustained participation and habit transformation.',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop',
    },
    {
      icon: <Plug className="w-6 h-6" />,
      title: 'Integration',
      description: 'Streamline linking gadgets, applications, and human resources platforms for a cohesive wellness experience. Application programming interface availability and smooth information synchronization. Integrate with Apple Health, Google Fit, Fitbit, Garmin, and 50+ additional fitness applications and devices. Human resources information system connection for automatic staff enrollment. Single sign-on assistance for organizational verification. Representational state transfer API for personalized connections. Webhook assistance for live event alerts. Information export functions for analytical tools. Smooth integration environment that functions with your current technical infrastructure.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Security & Compliance',
      description: 'Emphasizing the safeguarding of staff information. Health Insurance Portability and Accountability Act, General Data Protection Regulation, and Service Organization Control II compliant with organizational-level protection. Complete encryption for confidential health information. Periodic security reviews and vulnerability assessments. Permission-based entry management and activity records. Information location choices for worldwide adherence. Confidentiality settings enable staff to manage information distribution. Routine backups and contingency planning. Adherence credentials and paperwork. Relied upon by organizations globally to secure sensitive staff health data.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop',
    },
  ];

  return (
    <section id="features" className="py-20 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Built on the foundation of Trigger, Response, and Recognition
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            A happier and healthier workforce, holistically
          </p>
        </div>

        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-8 text-center">
            Holistic Wellness Trackers
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {holisticFeatures.map((feature, index) => (
              <FeatureCard key={index} {...feature} id={feature.id} />
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-8 text-center">
            Other Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherFeatures.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;

